import { editPlaybackFact } from "../api/client";
import type { PlaybackFact } from "../api/types";

export class PlaybackCard {
  private container: HTMLElement;
  private sessionId: string;

  constructor(container: HTMLElement, sessionId: string, facts: PlaybackFact[]) {
    this.container = container;
    this.sessionId = sessionId;
    this._render(facts);
  }

  private _render(facts: PlaybackFact[]): void {
    this.container.innerHTML = `
      <div class="playback-header">
        <h3>Here's what I understood</h3>
        <span class="badge">Review and correct anything that's off</span>
      </div>
      ${facts.map((f) => this._factRow(f)).join("")}
    `;

    this.container.querySelectorAll<HTMLButtonElement>(".pf-edit").forEach((btn) => {
      btn.addEventListener("click", () => this._startEdit(btn));
    });
  }

  private _factRow(fact: PlaybackFact): string {
    return `
      <div class="playback-fact" data-fact-id="${fact.id}">
        <div class="pf-label">${fact.label}</div>
        <div class="pf-value">${this._escape(fact.value)}</div>
        <button class="pf-edit" data-fact-id="${fact.id}" type="button">Edit</button>
      </div>
    `;
  }

  private _startEdit(btn: HTMLButtonElement): void {
    const factId = btn.dataset["factId"]!;
    const row = this.container.querySelector<HTMLElement>(`.playback-fact[data-fact-id="${factId}"]`);
    if (!row) return;

    const valueEl = row.querySelector<HTMLElement>(".pf-value")!;
    const currentValue = valueEl.textContent ?? "";

    const input = document.createElement("input");
    input.type = "text";
    input.value = currentValue;
    input.className = "pf-edit-input";
    input.setAttribute("aria-label", "Edit value");
    input.style.cssText = `
      flex:1;
      font-size:13.5px;
      padding:6px 10px;
      border:1px solid var(--border-medium-raw);
      border-radius:var(--radius-sm);
      font-family:var(--font);
      background:transparent;
      color:var(--text-primary);
      outline:none;
      transition:border-color 0.2s ease;
    `;

    input.addEventListener("focus", () => { input.style.borderColor = "var(--swo-blue)"; });
    input.addEventListener("blur", () => { input.style.borderColor = "var(--border-medium-raw)"; });

    valueEl.replaceWith(input);
    btn.textContent = "Save";
    input.focus();
    input.select();

    const save = async () => {
      const newValue = input.value.trim();
      if (!newValue) return;
      btn.textContent = "Saving...";
      btn.disabled = true;
      try {
        const updated = await editPlaybackFact(this.sessionId, factId, newValue);
        const newValueEl = document.createElement("div");
        newValueEl.className = "pf-value";
        newValueEl.textContent = updated.value;
        input.replaceWith(newValueEl);
        btn.textContent = "Edit";
        btn.disabled = false;
      } catch {
        input.style.borderColor = "var(--swo-red)";
        btn.textContent = "Edit";
        btn.disabled = false;
      }
    };

    btn.addEventListener("click", save, { once: true });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") save();
      if (e.key === "Escape") {
        const revertEl = document.createElement("div");
        revertEl.className = "pf-value";
        revertEl.textContent = currentValue;
        input.replaceWith(revertEl);
        btn.textContent = "Edit";
      }
    });
  }

  private _escape(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
}
